import { useEffect } from 'react';
import { SITE_URL, SITE_NAME } from '@/lib/seo';

interface HomeSchemaProps {
  lang: string;
  url: string;
}

export function HomeSchema({ lang, url }: HomeSchemaProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: SITE_NAME,
          url: `${SITE_URL}/`,
          description: 'Independent informational resource for CSSBuy Spreadsheet with links, guides, categories, QC, shipping and warehouse information.',
        },
        {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: `${SITE_URL}/`,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${SITE_URL}/?search={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'WebPage',
          '@id': url,
          url: url,
          name: 'CSSBuy Spreadsheet 2026 – Links, Guias, QC e Recursos',
          description: 'Encontre o CSSBuy Spreadsheet com links, categorias, guias de compra, QC, shipping, warehouse e informações úteis para compradores. Recurso independente.',
          inLanguage: lang === 'pt' ? 'pt-BR' : lang === 'de' ? 'de-DE' : 'en',
          isPartOf: { '@id': `${SITE_URL}/` },
        },
      ],
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [lang, url]);

  return null;
}

interface FAQSchemaProps {
  faqs: { question: string; answer: string }[];
}

export function FAQSchema({ faqs }: FAQSchemaProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer,
        },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [faqs]);

  return null;
}

interface ArticleSchemaProps {
  title: string;
  description: string;
  url: string;
  lang: string;
  datePublished: string;
  dateModified: string;
}

export function ArticleSchema({ title, description, url, lang, datePublished, dateModified }: ArticleSchemaProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: description,
      url: url,
      inLanguage: lang === 'pt' ? 'pt-BR' : lang === 'de' ? 'de-DE' : 'en',
      author: {
        '@type': 'Organization',
        name: `${SITE_NAME} Editorial Team`,
      },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
      },
      datePublished: datePublished,
      dateModified: dateModified,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [title, description, url, lang, datePublished, dateModified]);

  return null;
}

interface BreadcrumbSchemaProps {
  items: { name: string; url: string }[];
}

export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [items]);

  return null;
}

interface PageSchemaProps {
  type: 'AboutPage' | 'ContactPage' | 'WebPage' | 'CollectionPage';
  name: string;
  description: string;
  url: string;
  lang: string;
}

export function PageSchema({ type, name, description, url, lang }: PageSchemaProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': type,
      name: name,
      description: description,
      url: url,
      inLanguage: lang === 'pt' ? 'pt-BR' : lang === 'de' ? 'de-DE' : 'en',
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/`,
        name: SITE_NAME,
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [type, name, description, url, lang]);

  return null;
}